import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Bell, Flag, CheckCircle2, MessageSquare, Plus, Check } from 'lucide-react'
import { useNotifications } from '@/hooks/useNotifications'
import { useMarkNotificationRead } from '@/hooks/mutations/useMarkNotificationRead'
import { useMarkAllRead } from '@/hooks/mutations/useMarkAllRead'
import type { Notification } from '@/types'

export default function NotificationBell() {
  const [open, setOpen] = useState(false)
  const [panelPos, setPanelPos] = useState<{ top: number; right: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const { data: notifications = [], unreadCount } = useNotifications()
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllRead()
  const navigate = useNavigate()

  // Calculate panel position relative to viewport when panel opens
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    setPanelPos({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    })
  }, [open])

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    function onClick(e: MouseEvent) {
      const target = e.target as Node
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open])

  function handleRowClick(n: Notification) {
    markRead.mutate(n.id as string)
    setOpen(false)
    if (n.link) navigate(n.link.replace(window.location.origin, ''))
  }

  const panel =
    open && panelPos
      ? createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Notifications"
            className="w-80 rounded-2xl shadow-xl border"
            style={{
              position: 'fixed',
              top: panelPos.top,
              right: panelPos.right,
              background: '#fff',
              borderColor: 'var(--line-2)',
              zIndex: 9999,
              maxHeight: 'calc(100vh - 80px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3 border-b flex-none"
              style={{ borderColor: 'var(--line-2)' }}
            >
              <span className="font-serif font-semibold text-base" style={{ color: 'var(--green-900)' }}>
                Notifications
              </span>
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllRead.mutate()}
                  disabled={markAllRead.isPending}
                  className="flex items-center gap-1 font-sans text-xs font-medium transition-opacity hover:opacity-70"
                  style={{ color: 'var(--green-700)' }}
                >
                  <Check size={12} />
                  Mark all read
                </button>
              )}
            </div>

            {/* List */}
            <div className="overflow-y-auto" style={{ maxHeight: '384px' }}>
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10 text-center">
                  <CheckCircle2 size={28} style={{ color: 'var(--green-500)', opacity: 0.5 }} />
                  <p className="font-sans text-sm" style={{ color: 'var(--ink-3)' }}>
                    You're all caught up.
                  </p>
                </div>
              ) : (
                notifications.map((n) => (
                  <NotificationRow
                    key={n.id as string}
                    notification={n}
                    onClick={() => handleRowClick(n)}
                  />
                ))
              )}
            </div>
          </div>,
          document.body
        )
      : null

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 flex items-center justify-center rounded-full transition-colors"
        style={{ color: 'var(--ink-3)' }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--green-050)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = '')}
        aria-label={unreadCount > 0 ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Bell size={18} strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span
            className="absolute top-1.5 right-1.5 flex items-center justify-center rounded-full font-sans font-semibold text-white"
            style={{
              background: 'var(--gold-500)',
              fontSize: '9px',
              minWidth: unreadCount > 9 ? '16px' : '13px',
              height: '13px',
              padding: '0 3px',
              lineHeight: 1,
            }}
            aria-hidden="true"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {panel}
    </>
  )
}

function NotificationRow({
  notification: n,
  onClick,
}: {
  notification: Notification
  onClick: () => void
}) {
  const isUnread = !n.read_at
  const payload = (n.payload as Record<string, unknown>) ?? {}

  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-start gap-3 px-4 py-3 transition-colors hover:bg-[--green-050]"
      style={{
        borderLeft: isUnread ? '3px solid var(--gold-500)' : '3px solid transparent',
        background: isUnread ? 'rgba(200,155,60,0.04)' : undefined,
      }}
    >
      <span
        className="mt-0.5 flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full"
        style={{ background: 'var(--green-050)' }}
      >
        <EventIcon eventType={n.event_type as string} payload={payload} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-sans text-sm leading-snug" style={{ color: 'var(--ink)', fontWeight: isUnread ? 600 : 400 }}>
          {notificationCopy(n.event_type as string, payload)}
        </p>
        <p className="mt-0.5 font-sans text-xs" style={{ color: 'var(--ink-3)' }}>
          {relativeTime(n.created_at as string)}
        </p>
      </div>
    </button>
  )
}

function EventIcon({ eventType, payload }: { eventType: string; payload: Record<string, unknown> }) {
  const color = 'var(--green-700)'
  const size = 14

  if (eventType === 'visit_note_flagged') {
    return <Flag size={size} style={{ color: 'var(--gold-700)' }} />
  }
  if (eventType === 'need_posted' && payload.priority === 'high_priority') {
    return <Flag size={size} style={{ color: 'var(--gold-500)' }} />
  }
  if (eventType === 'need_posted') {
    return <Plus size={size} style={{ color }} />
  }
  if (eventType === 'need_claimed') {
    return <CheckCircle2 size={size} style={{ color }} />
  }
  if (eventType === 'visit_note_posted') {
    return <MessageSquare size={size} style={{ color }} />
  }
  return <Bell size={size} style={{ color }} />
}

function notificationCopy(eventType: string, payload: Record<string, unknown>): string {
  const title = payload.title as string | undefined
  switch (eventType) {
    case 'need_posted':
      return title ? `New need posted: "${title}"` : 'A new need was posted in your village'
    case 'need_claimed':
      return title ? `"${title}" was claimed` : 'A need was claimed'
    case 'visit_note_posted':
      return 'A new visit note was posted'
    case 'visit_note_flagged':
      return 'A visit note was flagged for your attention'
    default:
      return 'New activity in your village'
  }
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'Yesterday'
  return `${days}d ago`
}
